import { useState, useEffect, useRef } from 'react';
import { BordOneToneIcon, NudosButton, NudosGenericModal } from 'components/NudosComponents';
import { IBulkUpdateMentionsDto, MENTION_STATUS } from 'types/dashboard.types';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { MENTION_BULK_ACTIONS, MENTION_CURRENT_STATES, IMentionToggleAction } from '../../utils/mentionsColumn.utils';
import { updateAllMentions } from 'services/dashboard.service';

import './MentionBulkActionsTrigger.scss';

interface IConfirmationModalProps {
  verificationText: string;
  handleClose: () => void;
  handleConfirmation: () => void;
  isLoading: boolean;
}

const ConfirmationModal = ({
  verificationText,
  handleClose,
  handleConfirmation,
  isLoading
}: IConfirmationModalProps) => {
  return (
    <NudosGenericModal
      modalContent={
        <div className="modalContainer" onClick={event => event.stopPropagation()}>
          <div className="modalIconContainer">
            <BordOneToneIcon variant="triangleAlertCircle" customWidth="46px" />
          </div>
          <p className="modalText">{verificationText}</p>
          <div className="modalButtonsContainer">
            <NudosButton
              handleClick={handleClose}
              colorPalette="secondary"
              buttonText="No, Cancelar"
              customClassName="p-8"
              customWidth="180px"
              customHeight="38px"
            />
            <NudosButton
              handleClick={handleConfirmation}
              buttonText="Si, Confirmar"
              customClassName="p-8"
              customWidth="180px"
              customHeight="38px"
              isButtonLoading={isLoading}
              isButtonDisabled={isLoading}
            />
          </div>
        </div>
      }
      closeModalCallback={handleClose}
      clickOutsideCallback={handleClose}
    />
  );
};

interface IUpdateObject {
  wasSeen: boolean;
  wasResolved: boolean;
}

const DEFAULT_UPDATE_OBJECT: IUpdateObject = {
  wasSeen: false,
  wasResolved: false
};

interface IMentionBulkActionsTriggerProps {
  status: MENTION_STATUS;
  allMentionIds: number[];
  reloadData: () => Promise<void>;
}

export const MentionBulkActionsTrigger = ({ status, allMentionIds, reloadData }: IMentionBulkActionsTriggerProps) => {
  const isMountedRef = useRef(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>('');
  const [updateObject, setUpdateObject] = useState<IUpdateObject>(DEFAULT_UPDATE_OBJECT);

  const { toggleSeen, toggleResolved } = MENTION_BULK_ACTIONS[status];
  const { seen, resolved } = MENTION_CURRENT_STATES[status];

  const handleUpdateMention = async (body: IBulkUpdateMentionsDto) => {
    try {
      setIsLoading(true);
      await updateAllMentions(body);
      await reloadData();
    } catch (error) {
      displayErrorNotification();
    } finally {
      if (!isMountedRef.current) return;
      setIsLoading(false);
    }
  };

  const handleShowConfirmationModal = (
    event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent,
    toggleAction: IMentionToggleAction
  ) => {
    event.stopPropagation();
    setUpdateObject(toggleAction.updateObject);
    setAlertText(toggleAction.alertText);
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = async () => {
    await handleUpdateMention({ mentionIds: allMentionIds, ...updateObject });
    await reloadData();
    if (!isMountedRef.current) return;
    setUpdateObject(DEFAULT_UPDATE_OBJECT);
    setShowConfirmationModal(false);
  };

  const handleCloseCancelationModal = () => {
    if (!isMountedRef.current) return;
    setUpdateObject(DEFAULT_UPDATE_OBJECT);
    setShowConfirmationModal(false);
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <div className="mentionBulkActionsTriggerContainer">
      <span>Marcar todos como:</span>

      <div className="checkboxesContainer">
        <div className="checkboxContainer" onClick={event => handleShowConfirmationModal(event, toggleSeen)}>
          <input
            type="checkbox"
            checked={seen}
            className="checkbox"
            onChange={event => handleShowConfirmationModal(event, toggleSeen)}
          />
          <span className="checkboxText">Leídos</span>
        </div>

        <div className="checkboxContainer" onClick={event => handleShowConfirmationModal(event, toggleResolved)}>
          <input
            type="checkbox"
            checked={resolved}
            className="checkbox"
            onChange={event => handleShowConfirmationModal(event, toggleResolved)}
          />
          <span className="checkboxText">Resueltos</span>
        </div>
      </div>

      {showConfirmationModal && (
        <ConfirmationModal
          verificationText={alertText}
          handleConfirmation={handleCloseConfirmationModal}
          handleClose={handleCloseCancelationModal}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};
